import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Alert,
  Avatar,
  Paper,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  TextField,
  InputAdornment,
  Popper,
  ClickAwayListener,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import {
  Refresh,
  ExpandMore,
  Business,
  AccountBalance,
  Gavel,
  Description,
  Home,
  PeopleAlt,
  VerifiedUser,
  Article,
  FolderOpen,
  AttachMoney,
  LocationOn,
  CheckCircle,
  Cancel,
  OpenInNew,
  Badge,
  AssignmentLate,
  Apartment,
  Search,
  KeyboardArrowDown,
  Clear,
} from "@mui/icons-material";
import api from "../../../api/axiosInterceptor";
import { SnackBarComponent } from "../../../Components/snackBar/snackBar";
import TableComponent from "../../../Components/TableComponent/TableComponent";


// ─── Theme tokens ─────────────────────────────────────────────────────────────
const T = {
  navy: "#0F2744",
  navyMid: "#1A3C5E",
  navyLight: "#234E78",
  gold: "#B8872A",
  goldLight: "#D4A843",
  goldPale: "#FDF6E9",
  bg: "#F4F6F9",
  surface: "#FFFFFF",
  border: "#DDE3EE",
  text: "#111827",
  textMid: "#374151",
  textSoft: "#6B7280",
  green: "#166534",
  greenBg: "#DCFCE7",
  red: "#991B1B",
  redBg: "#FEE2E2",
  blueSoft: "#EFF4FB",
};

const TABLE_SX = {
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: T.navyMid,
    color: "#fff",
    fontWeight: "bold",
    fontSize: 12,
  },
  "& .MuiDataGrid-row:hover": { bgcolor: T.blueSoft },
  fontSize: 12,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const ActiveChip = ({ active }) => (
  <Chip
    icon={active ? <CheckCircle sx={{ fontSize: 13 }} /> : <Cancel sx={{ fontSize: 13 }} />}
    label={active ? "Active" : "Inactive"}
    size="small"
    sx={{
      bgcolor: active ? T.greenBg : T.redBg,
      color: active ? T.green : T.red,
      fontWeight: 700,
      fontSize: 10.5,
      height: 22,
    }}
  />
);

const ViewDocBtn = ({ url, label = "View" }) =>
  url ? (
    <Button
      size="small"
      variant="contained"
      endIcon={<OpenInNew sx={{ fontSize: 11 }} />}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        fontSize: 10.5,
        textTransform: "none",
        bgcolor: T.navyMid,
        color: "#fff",
        py: 0.25,
        px: 1.2,
        minWidth: 0,
        boxShadow: "none",
        "&:hover": { bgcolor: T.navyLight, boxShadow: "none" },
      }}
    >
      {label}
    </Button>
  ) : (
    <Typography sx={{ fontSize: 12, color: T.textSoft }}>—</Typography>
  );

const InfoRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      py: 0.75,
      borderBottom: `1px solid ${T.border}`,
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Typography
      sx={{
        minWidth: 185,
        fontSize: 11.5,
        fontWeight: 600,
        color: T.textSoft,
        textTransform: "uppercase",
        letterSpacing: 0.4,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13, color: T.text, flex: 1 }}>{value ?? "—"}</Typography>
  </Box>
);

const Empty = ({ msg = "No records found" }) => (
  <Typography sx={{ color: T.textSoft, fontSize: 12.5, fontStyle: "italic", py: 1.5 }}>
    {msg}
  </Typography>
);

const UserCard = ({ user, role, isActive }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      p: 1.5,
      border: `1px solid ${T.border}`,
      borderRadius: 2,
      bgcolor: isActive ? T.greenBg : T.bg,
      mb: 1,
    }}
  >
    <Avatar
      sx={{ width: 34, height: 34, bgcolor: T.navyMid, fontSize: 13, fontWeight: 800 }}
    >
      {user?.full_name?.[0]?.toUpperCase() ?? "?"}
    </Avatar>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.text, lineHeight: 1.3 }}>
        {user?.full_name ?? "N/A"}
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          color: T.textSoft,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {user?.email ?? ""}
      </Typography>
    </Box>
    {role && (
      <Chip
        label={role}
        size="small"
        sx={{ fontSize: 10, height: 20, bgcolor: T.blueSoft, color: T.navyMid, fontWeight: 700 }}
      />
    )}
    <ActiveChip active={isActive} />
  </Box>
);

const Section = ({
  icon,
  title,
  accent = T.navyMid,
  children,
  defaultExpanded = true,
  count,
}) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    disableGutters
    elevation={0}
    sx={{
      border: `1px solid ${T.border}`,
      borderRadius: "8px !important",
      mb: 1.5,
      "&:before": { display: "none" },
      overflow: "hidden",
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMore sx={{ color: "#fff", fontSize: 20 }} />}
      sx={{
        bgcolor: accent,
        minHeight: 44,
        px: 2.5,
        "& .MuiAccordionSummary-content": { my: 0.75, alignItems: "center", gap: 1.2 },
      }}
    >
      <Box sx={{ color: "#fff", display: "flex", alignItems: "center" }}>{icon}</Box>
      <Typography
        sx={{ color: "#fff", fontWeight: 700, fontSize: 13, letterSpacing: 0.2, flex: 1 }}
      >
        {title}
      </Typography>
      {count !== undefined && (
        <Chip
          label={count}
          size="small"
          sx={{
            bgcolor: "rgba(255,255,255,0.22)",
            color: "#fff",
            fontWeight: 700,
            fontSize: 11,
            height: 20,
            mr: 1,
          }}
        />
      )}
    </AccordionSummary>
    <AccordionDetails sx={{ p: 2, bgcolor: T.surface }}>{children}</AccordionDetails>
  </Accordion>
);

// ─── Light FE Dropdown ────────────────────────────────────────────────────────
const FESelector = ({ value, onChange }) => {
  const [feList, setFeList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const inputRef = React.useRef(null);

  // Fetch FE list on mount
  useEffect(() => {
    const fetchFEList = async () => {
      setLoadingList(true);
      try {
        const res = await api.get("/fe-mgmt/fe-base/");
        // Support both paginated { results: [...] } and plain array responses
        const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
        setFeList(list);
      } catch {
        console.error("Failed to fetch FE list");
      } finally {
        setLoadingList(false);
      }
    };
    fetchFEList();
  }, []);

  const selected = feList.find((f) => f.id === value) ?? null;

  const filtered = feList.filter((f) => {
    const q = search.toLowerCase();
    return (
      f.name_of_fe?.toLowerCase().includes(q) ||
      f.fe_code?.code?.toLowerCase().includes(q) ||
      f.address?.toLowerCase().includes(q)
    );
  });

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    setOpen(true);
    setSearch("");
    // Focus the search box on next tick
    setTimeout(() => {
      document.getElementById("fe-search-input")?.focus();
    }, 50);
  };

  const handleSelect = (fe) => {
    onChange(fe.id);
    setOpen(false);
    setSearch("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setSearch("");
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: "relative", minWidth: 320 }}>
        {/* Trigger button */}
        <Box
          ref={inputRef}
          onClick={handleOpen}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.9,
            bgcolor: "#fff",
            border: `1.5px solid ${open ? T.gold : T.border}`,
            borderRadius: 2,
            cursor: "pointer",
            transition: "border-color 0.15s",
            minHeight: 42,
            "&:hover": { borderColor: T.gold },
          }}
        >
          <Business sx={{ fontSize: 17, color: T.gold, flexShrink: 0 }} />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {selected ? (
              <>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: T.text,
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {selected.name_of_fe}
                </Typography>
                {selected.fe_code?.code && (
                  <Typography sx={{ fontSize: 10.5, color: T.textSoft }}>
                    {selected.fe_code.code}
                    {selected.fe_code.fe_type ? ` · ${selected.fe_code.fe_type}` : ""}
                  </Typography>
                )}
              </>
            ) : (
              <Typography sx={{ fontSize: 13, color: T.textSoft }}>
                {loadingList ? "Loading establishments…" : "Select Financial Establishment"}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            {loadingList && <CircularProgress size={14} sx={{ color: T.gold }} />}
            {selected && !loadingList && (
              <IconButton size="small" onClick={handleClear} sx={{ p: 0.2 }}>
                <Clear sx={{ fontSize: 15, color: T.textSoft }} />
              </IconButton>
            )}
            <KeyboardArrowDown
              sx={{
                fontSize: 18,
                color: T.textSoft,
                transform: open ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            />
          </Box>
        </Box>

        {/* Dropdown panel */}
        <Popper
          open={open}
          anchorEl={inputRef.current}
          placement="bottom-start"
          style={{ zIndex: 1400, width: inputRef.current?.offsetWidth ?? 320 }}
        >
          <Paper
            elevation={6}
            sx={{
              mt: 0.5,
              borderRadius: 2,
              border: `1px solid ${T.border}`,
              overflow: "hidden",
              maxHeight: 360,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Search */}
            <Box sx={{ p: 1, borderBottom: `1px solid ${T.border}`, bgcolor: "#FAFBFC" }}>
              <TextField
                id="fe-search-input"
                size="small"
                placeholder="Search by name, code or address…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 16, color: T.textSoft }} />
                    </InputAdornment>
                  ),
                  sx: { fontSize: 12.5, bgcolor: "#fff", borderRadius: 1.5 },
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            </Box>

            {/* List */}
            <List
              dense
              disablePadding
              sx={{ overflowY: "auto", flex: 1 }}
            >
              {filtered.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography sx={{ fontSize: 12.5, color: T.textSoft, fontStyle: "italic" }}>
                        {search ? "No matching establishments" : "No establishments available"}
                      </Typography>
                    }
                  />
                </ListItem>
              ) : (
                filtered.map((fe) => {
                  const isSelected = fe.id === value;
                  return (
                    <ListItem
                      key={fe.id}
                      button
                      onClick={() => handleSelect(fe)}
                      sx={{
                        px: 2,
                        py: 1,
                        bgcolor: isSelected ? T.blueSoft : "transparent",
                        borderLeft: isSelected ? `3px solid ${T.gold}` : "3px solid transparent",
                        "&:hover": { bgcolor: T.blueSoft },
                        cursor: "pointer",
                        transition: "background 0.1s",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, width: "100%" }}>
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: isSelected ? T.navy : T.navyLight,
                            fontSize: 11,
                            fontWeight: 800,
                            flexShrink: 0,
                          }}
                        >
                          {fe.name_of_fe?.[0]?.toUpperCase() ?? "F"}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 12.5,
                              fontWeight: isSelected ? 700 : 500,
                              color: T.text,
                              lineHeight: 1.3,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {fe.name_of_fe}
                          </Typography>
                          {(fe.fe_code?.code || fe.address) && (
                            <Typography
                              sx={{
                                fontSize: 10.5,
                                color: T.textSoft,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {[fe.fe_code?.code, fe.address].filter(Boolean).join(" · ")}
                            </Typography>
                          )}
                        </Box>
                        {isSelected && (
                          <CheckCircle sx={{ fontSize: 15, color: T.gold, flexShrink: 0 }} />
                        )}
                      </Box>
                    </ListItem>
                  );
                })
              )}
            </List>

            {/* Footer count */}
            {feList.length > 0 && (
              <Box
                sx={{
                  px: 2,
                  py: 0.75,
                  borderTop: `1px solid ${T.border}`,
                  bgcolor: "#FAFBFC",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: 10.5, color: T.textSoft }}>
                  {filtered.length} of {feList.length} establishments
                </Typography>
                {search && (
                  <Button
                    size="small"
                    onClick={() => setSearch("")}
                    sx={{ fontSize: 10.5, textTransform: "none", color: T.navyMid, py: 0, minWidth: 0 }}
                  >
                    Clear filter
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const FinancialEstablishmentDetail = () => {
  const [selectedFe, setSelectedFe] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const fetchDetail = useCallback(async (feId) => {
    if (!feId) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res = await api.get(`/fe-mgmt/fe-detail/${feId}/`);
    //   console.log("FE Detail Response:", res.data);
      setData(res.data);
    } catch {
      setError("Failed to load FE details. Please try again.");
      setSnack({ open: true, message: "Failed to load details", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFeChange = useCallback((id) => {
    setSelectedFe(id || null);
  }, []);

  useEffect(() => {
    if (selectedFe) fetchDetail(selectedFe);
    else setData(null);
  }, [selectedFe, fetchDetail]);

  // ── Column definitions ──
  const officialCols = [
    {
      field: "catogary",
      headerName: "Category",
      flex: 1,
      renderCell: (p) => (
        <Chip label={p.value} size="small" sx={{ fontSize: 10, bgcolor: T.blueSoft, color: T.navyMid }} />
      ),
    },
    { field: "director_name", headerName: "Name", flex: 1.2 },
    { field: "directors_phone_no", headerName: "Phone", flex: 1 },
    { field: "director_email", headerName: "Email", flex: 1.5 },
    { field: "direct_address", headerName: "Address", flex: 2 },
  ];

  const bankCols = [
    {
      field: "account_no",
      headerName: "Account No",
      flex: 1.2,
      renderCell: (p) => (
        <Typography sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: 12 }}>
          {p.value}
        </Typography>
      ),
    },
    { field: "bank_name", headerName: "Bank Name", flex: 1.2 },
    { field: "branch_name", headerName: "Branch", flex: 1 },
    {
      field: "ifsc_code",
      headerName: "IFSC",
      flex: 0.9,
      renderCell: (p) => (
        <Typography sx={{ fontFamily: "monospace", fontSize: 12 }}>{p.value}</Typography>
      ),
    },
  ];

  const courtCols = [
    {
      field: "case_no",
      headerName: "Case No",
      flex: 0.9,
      renderCell: (p) => (
        <Typography sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: 12 }}>
          {p.value}
        </Typography>
      ),
    },
    { field: "court_name", headerName: "Court", flex: 1.2 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      renderCell: (p) => (
        <Chip
          label={p.value}
          size="small"
          sx={{ fontSize: 10, bgcolor: T.blueSoft, color: T.navyMid, fontWeight: 600 }}
        />
      ),
    },
    { field: "description", headerName: "Description", flex: 2 },
    {
      field: "doc",
      headerName: "Document",
      flex: 0.7,
      renderCell: (p) => <ViewDocBtn url={p.value} />,
    },
  ];

  const docDateCols = [
    { field: "fy_year", headerName: "FY Year", flex: 0.8 },
    { field: "file_type", headerName: "File Type", flex: 1 },
    { field: "doc", headerName: "Document", flex: 0.7, renderCell: (p) => <ViewDocBtn url={p.value} /> },
    {
      field: "created_at",
      headerName: "Date",
      flex: 0.9,
      valueFormatter: (p) => p.value?.slice(0, 10),
    },
  ];

  const firCols = [
    { field: "description", headerName: "Description", flex: 3 },
    { field: "created_at", headerName: "Date", flex: 0.9, valueFormatter: (p) => p.value?.slice(0, 10) },
    { field: "doc", headerName: "Document", flex: 0.7, renderCell: (p) => <ViewDocBtn url={p.value} /> },
  ];

  const propCols = [
    { field: "property_type", headerName: "Type", flex: 0.9 },
    { field: "location", headerName: "Location", flex: 1.5 },
    { field: "date_of_aquisition", headerName: "Acquired On", flex: 0.9 },
    { field: "value", headerName: "Value", flex: 0.9 },
    { field: "type_of_aquisition", headerName: "Mode", flex: 0.9 },
    { field: "doc", headerName: "Document", flex: 0.7, renderCell: (p) => <ViewDocBtn url={p.value} /> },
  ];

  return (
    <Box sx={{ bgcolor: T.bg, minHeight: "100vh" }}>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: T.navy,
          borderBottom: `3px solid ${T.gold}`,
          px: { xs: 2, md: 4 },
          py: 2,
        }}
      >
        <Box
          sx={{
            maxWidth: 1240,
            mx: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {/* Title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: 2,
                bgcolor: T.gold,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Business sx={{ color: "#fff", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  color: T.goldLight,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 2.5,
                  textTransform: "uppercase",
                  lineHeight: 1,
                }}
              >
                Records & Intelligence
              </Typography>
              <Typography
                sx={{ color: "#fff", fontSize: 18, fontWeight: 800, letterSpacing: -0.3, lineHeight: 1.4 }}
              >
                Financial Establishment Database and Coding
              </Typography>
            </Box>
          </Box>

          {/* Selector — light dropdown sits in dark header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FESelector value={selectedFe} onChange={handleFeChange} />
            {selectedFe && (
              <Tooltip title="Refresh">
                <IconButton
                  onClick={() => fetchDetail(selectedFe)}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    color: "#fff",
                    "&:hover": { bgcolor: T.gold },
                  }}
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      {/* ── Body ─────────────────────────────────────────────────────────────── */}
      <Box sx={{ maxWidth: 1240, mx: "auto", px: { xs: 2, md: 3 }, py: 3 }}>
        {/* Empty state */}
        {!selectedFe && !loading && (
          <Paper
            elevation={0}
            sx={{
              border: `2px dashed ${T.border}`,
              borderRadius: 3,
              p: 7,
              textAlign: "center",
              bgcolor: T.surface,
            }}
          >
            <Business sx={{ fontSize: 60, color: T.border, mb: 2 }} />
            <Typography sx={{ color: T.textSoft, fontSize: 15, fontWeight: 500 }}>
              Select a Financial Establishment above to view its complete record
            </Typography>
          </Paper>
        )}

        {loading && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 10, gap: 2 }}>
            <CircularProgress sx={{ color: T.navyMid }} size={40} />
            <Typography sx={{ color: T.textSoft, fontSize: 13 }}>
              Loading establishment details…
            </Typography>
          </Box>
        )}

        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {data && !loading && (
          <Box>
            {/* 1 ── Base Info */}
            <Section
              icon={<Business fontSize="small" />}
              title="Financial Establishment — Base Information"
              accent={T.navy}
              defaultExpanded
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={8}>
                  <Box
                    sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: 2,
                      border: `2px solid ${T.gold}`,
                      bgcolor: T.goldPale,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Avatar
                      sx={{ width: 50, height: 50, bgcolor: T.navy, fontSize: 19, fontWeight: 800 }}
                    >
                      {data.name_of_fe?.[0]?.toUpperCase() ?? "FE"}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontSize: 17, fontWeight: 800, color: T.text }}>
                        {data.name_of_fe ?? "—"}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: T.textSoft }}>
                        {[
                          data.fe_code?.code,
                          data.fe_code?.fe_type,
                          data.fe_code?.registered_type,
                        ]
                          .filter(Boolean)
                          .join("  ·  ")}
                      </Typography>
                    </Box>
                  </Box>
                  <InfoRow label="Address" value={data.address} />
                  <InfoRow label="Company Phone" value={data.company_ph} />
                  <InfoRow label="Company Email" value={data.company_email} />
                  <InfoRow label="Date of Registration" value={data.date_of_registration} />
                  <InfoRow label="Date of FIR" value={data.date_of_fir} />
                  <InfoRow label="Date of Charge Sheet" value={data.date_of_charge_sheet} />
                  <InfoRow label="Fraudulent Scheme" value={data.type_of_fraudulent_scheme?.name} />
                  <InfoRow label="Business Type" value={data.type_of_bussiness?.name} />
                  {/* <InfoRow label="Created By" value={data.created_by?.full_name} /> */}
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: T.blueSoft,
                      border: `1px solid ${T.border}`,
                      mb: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 800,
                        color: T.navyMid,
                        letterSpacing: 1.2,
                        textTransform: "uppercase",
                        mb: 1.5,
                      }}
                    >
                      FE Code Details
                    </Typography>
                    <InfoRow label="Code" value={data.fe_code?.code} />
                    <InfoRow label="FE Type" value={data.fe_code?.fe_type} />
                    <InfoRow label="Registered Type" value={data.fe_code?.registered_type} />
                  </Paper>

                  {data.validity?.length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: T.greenBg,
                        border: `1px solid #A7F3D0`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 10.5,
                          fontWeight: 800,
                          color: T.green,
                          letterSpacing: 1.2,
                          textTransform: "uppercase",
                          mb: 1.5,
                        }}
                      >
                        Validity Period
                      </Typography>
                      {data.validity.map((v) => (
                        <Box key={v.id}>
                          <InfoRow label="Normal Start" value={v.normal_start_date} />
                          <InfoRow label="Normal End" value={v.normal_end_date} />
                          {v.delay_start_date && (
                            <InfoRow label="Delay Start" value={v.delay_start_date} />
                          )}
                          {v.delay_end_date && (
                            <InfoRow label="Delay End" value={v.delay_end_date} />
                          )}
                          <Box sx={{ mt: 1 }}>
                            <ActiveChip active={v.is_active} />
                          </Box>
                        </Box>
                      ))}
                    </Paper>
                  )}
                </Grid>
              </Grid>
            </Section>

            {/* 2 ── CA */}
            <Section
              icon={<VerifiedUser fontSize="small" />}
              title="Competent Authority(CA)"
              accent="#1D6A4A"
            >
              {data.ca ? (
                <UserCard user={data.ca.ca} role="CA" isActive={data.ca.is_active} />
              ) : (
                <Empty msg="No CA assigned" />
              )}
            </Section>

            {/* 3 ── Officials */}
            <Section
              icon={<PeopleAlt fontSize="small" />}
              title="Officials / Directors"
              accent="#2D5986"
              count={data.officials?.length}
            >
              {data.officials?.length > 0 ? (
                <TableComponent
                  columns={officialCols}
                  rows={data.officials}
                  manageColumnStatus
                  enableSort
                  sx={TABLE_SX}
                />
              ) : (
                <Empty />
              )}
            </Section>

            {/* 4 ── Assigned Officers */}
            <Section
              icon={<Badge fontSize="small" />}
              title="Assigned Officers"
              accent="#4A3080"
            >
              <Grid container spacing={2}>
                {[
                //   { label: "Shirastedar", items: data.shirastedar, key: "shirastedar" },
                //   { label: "Tahsildar", items: data.tahsildar, key: "tahsildar" },
                //   { label: "AC", items: data.ac, key: "ac" },
                //   { label: "JS", items: data.js, key: "js" },
                  { label: "Ver-1", items: data.ver1, key: "ver1" },
                  { label: "Ver-2", items: data.ver2, key: "ver2" },
                ].map(({ label, items, key }) => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 800,
                        color: T.navyMid,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        mb: 1,
                      }}
                    >
                      {label}
                    </Typography>
                    {items?.length > 0 ? (
                      items.map((item) => (
                        <UserCard
                          key={item.id}
                          user={item[key]}
                          role={label}
                          isActive={item.is_active}
                        />
                      ))
                    ) : (
                      <Empty msg={`No ${label} assigned`} />
                    )}
                  </Grid>
                ))}
              </Grid>
            </Section>

            {/* 5 ── Super Staff */}
            {data.super_staff?.length > 0 && (
              <Section
                icon={<VerifiedUser fontSize="small" />}
                title="Super Staff"
                accent="#7A3B1E"
                count={data.super_staff.length}
              >
                <Grid container spacing={1.5}>
                  {data.super_staff.map((s) => (
                    <Grid item xs={12} md={6} key={s.id}>
                      <Paper
                        elevation={0}
                        sx={{ p: 2, border: `1px solid ${T.border}`, borderRadius: 2 }}
                      >
                        <UserCard
                          user={s.super_staff}
                          role="Super Staff"
                          isActive={s.is_active}
                        />
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7, mt: 1.2 }}>
                          {[
                            ["Summons", s.is_summons_creation_permission],
                            ["Warrant", s.is_warrant_creation_permission],
                            ["Publication", s.is_publication_permission],
                            ["Asset Valuation", s.is_asset_valuation_permission],
                            ["Seized Movable", s.register_of_seized_movable_property_permission],
                            [
                              "Categorization",
                              s.categorization_of_seized_movable_property_permission,
                            ],
                          ].map(([perm, val]) => (
                            <Chip
                              key={perm}
                              label={perm}
                              size="small"
                              sx={{
                                fontSize: 10,
                                bgcolor: val ? T.greenBg : T.redBg,
                                color: val ? T.green : T.red,
                                fontWeight: 700,
                                height: 20,
                              }}
                            />
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Section>
            )}

            {/* 6 ── Bank Accounts */}
            <Section
              icon={<AccountBalance fontSize="small" />}
              title="Bank Accounts"
              accent="#1A5C5C"
              count={data.bank_accounts?.length}
            >
              {data.bank_accounts?.length > 0 ? (
                <TableComponent
                  columns={bankCols}
                  rows={data.bank_accounts}
                  manageColumnStatus
                  enableSort
                  sx={TABLE_SX}
                />
              ) : (
                <Empty />
              )}
            </Section>

            {/* 7 ── Business Premises */}
            <Section
              icon={<Apartment fontSize="small" />}
              title="Business Premises"
              accent="#2C5F2E"
              count={data.business_premises?.length}
            >
              {data.business_premises?.length > 0 ? (
                <Grid container spacing={2}>
                  {data.business_premises.map((b) => (
                    <Grid item xs={12} sm={6} key={b.id}>
                      <Paper
                        elevation={0}
                        sx={{ p: 2, border: `1px solid ${T.border}`, borderRadius: 2 }}
                      >
                        <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: T.text, mb: 1 }}>
                          {b.name ?? "Premises"}
                        </Typography>
                        <InfoRow label="Address" value={b.address} />
                        <InfoRow label="Contact" value={b.contact_number} />
                        <InfoRow label="Email" value={b.email} />
                        <InfoRow label="Website" value={b.website} />
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Empty />
              )}
            </Section>

            {/* 8 ── Capital Contribution */}
            <Section
              icon={<AttachMoney fontSize="small" />}
              title="Capital Contribution"
              accent="#7A5800"
              count={data.capital_contributions?.length}
            >
              {data.capital_contributions?.length > 0 ? (
                data.capital_contributions.map((c) => (
                  <Box key={c.id} sx={{ mb: 0.5 }}>
                    <InfoRow label="Paid Capital" value={c.paid_capital} />
                    <InfoRow label="Shares / Debentures" value={c.shares_debentures} />
                  </Box>
                ))
              ) : (
                <Empty />
              )}
            </Section>

            {/* 9 ── FIR */}
            <Section
              icon={<AssignmentLate fontSize="small" />}
              title="FIR Records"
              accent="#8B0000"
              count={data.fir_cases?.length}
            >
              {data.fir_cases?.length > 0 ? (
                <TableComponent
                  columns={firCols}
                  rows={data.fir_cases}
                  manageColumnStatus
                  enableSort
                  sx={TABLE_SX}
                />
              ) : (
                <Empty />
              )}
            </Section>

            {/* 10 ── Court Cases */}
            <Section
              icon={<Gavel fontSize="small" />}
              title="Court Cases"
              accent="#4A1942"
              count={data.court_cases?.length}
            >
              {data.court_cases?.length > 0 ? (
                <TableComponent
                  columns={courtCols}
                  rows={data.court_cases}
                  manageColumnStatus
                  enableSort
                  sx={TABLE_SX}
                />
              ) : (
                <Empty />
              )}
            </Section>

            {/* 11 ── Charge Sheets */}
            <Section
              icon={<Description fontSize="small" />}
              title="Charge Sheets"
              accent={T.navyMid}
              defaultExpanded={false}
              count={data.charge_sheets?.length}
            >
              {data.charge_sheets?.length > 0 ? (
                <TableComponent
                  columns={firCols}
                  rows={data.charge_sheets}
                  manageColumnStatus
                  enableSort
                  sx={TABLE_SX}
                />
              ) : (
                <Empty />
              )}
            </Section>

            {/* 12 ── Annual Accounts */}
            <Section
              icon={<Article fontSize="small" />}
              title="Annual Account Operations"
              accent={T.navyMid}
              defaultExpanded={false}
              count={data.annual_accounts?.length}
            >
              {data.annual_accounts?.length > 0 ? (
                <TableComponent
                  columns={docDateCols}
                  rows={data.annual_accounts}
                  manageColumnStatus
                  enableSort
                  sx={TABLE_SX}
                />
              ) : (
                <Empty />
              )}
            </Section>

            {/* 13 ── Audit Reports */}
            <Section
              icon={<FolderOpen fontSize="small" />}
              title="Audit Reports"
              accent={T.navyMid}
              defaultExpanded={false}
              count={data.audit_reports?.length}
            >
              {data.audit_reports?.length > 0 ? (
                <TableComponent
                  columns={docDateCols}
                  rows={data.audit_reports}
                  manageColumnStatus
                  enableSort
                  sx={TABLE_SX}
                />
              ) : (
                <Empty />
              )}
            </Section>

            {/* 14 ── KYC Docs */}
            <Section
              icon={<FolderOpen fontSize="small" />}
              title="KYC & Regulatory Documents"
              accent="#444"
            >
              {data.documents?.length > 0 ? (
                data.documents.map((d) => (
                  <Grid container spacing={2} key={d.id} sx={{ mb: 1 }}>
                    {[
                      { label: "FRA", url: d.fra },
                      { label: "GST-IN", url: d.gst_in },
                      { label: "e-KYC Document", url: d.e_kyc_doc },
                      { label: "Payar / Beneficiary Proof", url: d.payar_benificiary_proof },
                    ].map(({ label, url }) => (
                      <Grid item xs={6} sm={3} key={label}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            border: `1px solid ${T.border}`,
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 10.5,
                              fontWeight: 700,
                              color: T.textSoft,
                              mb: 1,
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            {label}
                          </Typography>
                          <ViewDocBtn url={url} label="View File" />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ))
              ) : (
                <Empty />
              )}
            </Section>

            {/* 15 ── Personal Properties */}
            <Section
              icon={<Home fontSize="small" />}
              title="Personal Properties"
              accent="#2E4A1E"
              defaultExpanded={false}
              count={data.personal_properties?.length}
            >
              {data.personal_properties?.length > 0 ? (
                <TableComponent
                  columns={propCols}
                  rows={data.personal_properties}
                  manageColumnStatus
                  enableSort
                  sx={TABLE_SX}
                />
              ) : (
                <Empty />
              )}
            </Section>

            {/* 16 ── Property Holdings */}
            <Section
              icon={<LocationOn fontSize="small" />}
              title="Property Holdings"
              accent="#2E4A1E"
              defaultExpanded={false}
              count={data.property_holdings?.length}
            >
              {data.property_holdings?.length > 0 ? (
                <Grid container spacing={1.5}>
                  {data.property_holdings.map((p) => (
                    <Grid item xs={12} sm={4} key={p.id}>
                      <Paper
                        elevation={0}
                        sx={{ p: 2, borderRadius: 2, border: `1px solid ${T.border}` }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.text, mb: 0.5 }}>
                          {p.propery_name ?? "—"}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: T.textSoft }}>
                          {p.property_location ?? "—"}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Empty />
              )}
            </Section>

            {/* 17 ── Sibling Companies */}
            <Section
              icon={<Business fontSize="small" />}
              title="Sibling / Associate Companies"
              accent={T.navyMid}
              defaultExpanded={false}
              count={data.sibling_companies?.length}
            >
              {data.sibling_companies?.length > 0 ? (
                data.sibling_companies.map((s) => (
                  <Paper
                    key={s.id}
                    elevation={0}
                    sx={{ p: 2, mb: 1.5, border: `1px solid ${T.border}`, borderRadius: 2 }}
                  >
                    <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 800, color: T.text }}>
                        {s.name_of_company}
                      </Typography>
                      <Chip
                        label={s.type_sibling}
                        size="small"
                        sx={{ fontSize: 10, bgcolor: T.blueSoft, color: T.navyMid, fontWeight: 700 }}
                      />
                    </Box>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={6}>
                        <InfoRow label="Address" value={s.address} />
                        <InfoRow label="Phone" value={s.phone_no} />
                        <InfoRow label="Email" value={s.email} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <InfoRow label="Director" value={s.director_name} />
                        <InfoRow label="Director Phone" value={s.directors_phone_no} />
                        <InfoRow label="Director Email" value={s.director_email} />
                        <InfoRow label="Director Address" value={s.direct_address} />
                      </Grid>
                    </Grid>
                  </Paper>
                ))
              ) : (
                <Empty />
              )}
            </Section>
          </Box>
        )}
      </Box>

      <SnackBarComponent
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
      />
    </Box>
  );
};

export default FinancialEstablishmentDetail;

